<template>
  <div>
    <b-row>
      <b-col md="3">
        <b-form-group
          label="Product"
        >
          <custom-v-select
            v-model="selectedProduct"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="filterProductOptions"
            label="display_text"
            placeholder="Search product..."
            :close-on-select="false"
            :deselect-from-dropdown="true"
            @search="onProductSearch"
            @input="handleProductChange"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group
          label="Company"
        >
          <custom-v-select
            id="company"
            v-model="selectedCompanies"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="companyOptions"
            label="text"
            placeholder="Select Company"
            multiple
            :close-on-select="false"
            :deselect-from-dropdown="true"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group
          label="MRP Range"
        >
          <b-row>
            <b-col cols="6">
              <b-form-input
                v-model.number="mrpMin"
                type="number"
                min="0"
                max="999999"
                placeholder="Min"
                @input="debouncedFiltersChange"
              />
            </b-col>
            <b-col cols="6">
              <b-form-input
                v-model.number="mrpMax"
                type="number"
                min="0"
                max="999999"
                placeholder="Max"
                @input="debouncedFiltersChange"
              />
            </b-col>
          </b-row>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group
          label="Status"
        >
          <custom-v-select
            v-model="selectedStatusOption"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="statusOptions"
            label="text"
            placeholder="Select Status"
            @input="filtersChangeHandler"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  VBTooltip, BRow, BCol, BFormGroup, BFormInput,
} from 'bootstrap-vue'
import axios from 'axios'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    CustomVSelect,
  },
  data() {
    return {
      mrpMin: null,
      mrpMax: null,
      companyOptions: [],
      selectedCompanies: [],
      products: [],
      selectedProduct: [],
      productOptions: [],
      filterProductOptions: [],
      productSearchTimeout: null,
      productCode: '',
      selectedStatusOption: null,
      statusOptions: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      codeInputTimeout: null,
      debounceTimer: null,
      initialLoadDone: false, // Track if initial products are loaded
    }
  },
  created() {
    this.fetchOptions()
    // Load initial products immediately
    this.fetchProducts()
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.codeInputTimeout) {
      clearTimeout(this.codeInputTimeout)
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  },
  methods: {
    debouncedFiltersChange() {
      // Clear the existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }

      // Set a new timer
      this.debounceTimer = setTimeout(() => {
        this.filtersChangeHandler()
      }, 500) // 500ms delay
    },
    onProductSearch(search) {
      if (this.productSearchTimeout) {
        clearTimeout(this.productSearchTimeout)
      }

      this.productSearchTimeout = setTimeout(() => {
        this.filterProducts(search)
      }, 300)
    },
    filterProducts(search) {
      if (!search || search.trim() === '') {
        // Show only first 15 items when no search
        this.filterProductOptions = this.productOptions.slice(0, 15)
      } else {
        // Filter products by search term
        const searchLower = search.toLowerCase()
        const filtered = this.productOptions.filter(product => product.name.toLowerCase().includes(searchLower)
          || (product.code && product.code.toLowerCase().includes(searchLower)))

        // Limit to first 50 results for performance
        this.filterProductOptions = filtered.slice(0, 50)
      }
    },

    async fetchProducts() {
      try {
        const productRes = await axios.get('/marg/product_options')
        this.productOptions = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          display_text: product.name,
        }))

        // Initialize with first 15 products
        this.filterProductOptions = this.productOptions.slice(0, 15)
      } catch (error) {
        this.errors = error.response?.data?.error || 'Failed to fetch products. Please try again.'
      }
    },

    handleProductChange(selectedProduct) {
      this.selectedProduct = selectedProduct || []
      this.filtersChangeHandler()
    },
    handleCodeInput() {
      // Clear previous timeout
      if (this.codeInputTimeout) {
        clearTimeout(this.codeInputTimeout)
      }

      // Clear product selection when typing code
      if (this.productCode && this.productCode.length > 0) {
        this.selectedProduct = null
      }

      // Only trigger filter if code has 4 or more characters
      if (this.productCode && this.productCode.length >= 4) {
        this.codeInputTimeout = setTimeout(() => {
          this.filtersChangeHandler()
        }, 500)
      } else if (!this.productCode || this.productCode.length === 0) {
        // If code is cleared, trigger filter update
        this.filtersChangeHandler()
      }
    },
    async fetchOptions() {
      try {
        // Fetch company options
        const companyRes = await axios.get('/company_options')
        this.companyOptions = companyRes.data.data.map(item => ({ text: item.text, id: item.text }))
      } catch (error) {
        this.errors = error.response?.data?.error || 'Failed to load filter options. Please refresh the page.'
      }
    },
    filtersChangeHandler() {
      const filtersdata = {}

      if (this.selectedProduct) {
        filtersdata.code = this.selectedProduct.code
      }

      if (this.selectedCompanies) {
        filtersdata.company = this.selectedCompanies.map(item => item.id)
      }

      if (this.selectedStatusOption) {
        if (this.selectedStatusOption.value === 'active') {
          filtersdata.minimum_order_quantity_applicable = 'True'
        } else if (this.selectedStatusOption.value === 'inactive') {
          filtersdata.minimum_order_quantity_applicable = 'False'
        }
      }

      // Handle MRP range
      if (this.mrpMin !== null || this.mrpMax !== null) {
        if (this.mrpMin !== null) {
          filtersdata.min_mrp = this.mrpMin
        }
        if (this.mrpMax !== null) {
          filtersdata.max_mrp = this.mrpMax
        }
      }

      this.$emit('filtersUpdated', filtersdata)
    },
  },
}
</script>
